/**
 * Player-detail query wrapper with a focus/app-state-aware 30s poll.
 *
 * WHY (SE-5039): The underlying query polls every 30s to keep player detail
 * fresh. Left unconditional, that timer keeps firing while the screen is
 * unfocused (another tab/route on top) or while the app is backgrounded —
 * waking the JS thread and issuing network work nobody is looking at, which
 * contributed to the Android ANR. `skipPollAttempt` is consulted by Apollo on
 * every tick: returning `true` skips that attempt without tearing down the
 * timer, so polling resumes automatically the moment the screen is focused and
 * the app is active again.
 * @module hooks/usePlayerDetailQuery
 */

import { QueryHookOptions } from "@apollo/client";
import { useIsFocused } from "expo-router/react-navigation";
import { useEffect, useRef } from "react";

import {
  PlayerDetailGetPlayerQuery,
  PlayerDetailGetPlayerQueryVariables,
  usePlayerDetailGetPlayerQuery,
} from "@/generated/graphql";
import { useAppStateListener } from "@/hooks/useAppStateListener";

/**
 * Runs the player-detail query with a 30s poll that pauses while the screen is
 * unfocused or the app is backgrounded.
 * @remarks
 * Also sets `errorPolicy: "all"` explicitly (rather than relying on the
 * ApolloClient default) so a network failure — e.g. a client-side timeout
 * (SE-5055) — is surfaced on the hook's `error` field instead of throwing.
 * That `error` is what the player-detail screen reads to render its error +
 * retry state, so pinning the policy here keeps the behavior local to the
 * hook and resilient to a future change in the global default.
 * Also forces `skip` when no player id is present (SE-5638). `playerId` reaches
 * the call sites from `useLocalSearchParams`, which is momentarily `undefined`
 * (or `""`) while a route mounts or tears down despite its `string` type.
 * Without this guard the 30s poll issues `PlayerDetailGetPlayer` with `id: ""`,
 * which the server rejects (`Variable "$id" got invalid value ""`) — that was
 * 100% of the query's production errors. Call sites guard too; this is the
 * belt-and-braces backstop so a future caller cannot reintroduce the bug.
 * The id is trimmed before the check because a URL like `/players/%20/compare`
 * yields `id: " "`, which is truthy but just as invalid to the server — and the
 * call sites' plain `!playerId` guards let it through.
 * A skipped query also stops polling (SE-8573).
 * @param options - Apollo query hook options (variables, skip, etc.)
 * @returns The Apollo query result for the player-detail query.
 */
export const usePlayerDetailQuery = (
  options: QueryHookOptions<
    PlayerDetailGetPlayerQuery,
    PlayerDetailGetPlayerQueryVariables
  >
) => {
  const isFocused = useIsFocused();
  const { isActive } = useAppStateListener();

  // Mirror the latest focus/active state into a ref so `skipPollAttempt` (a
  // stable closure) can read it on every tick without re-subscribing the poll.
  // The ref is updated in an effect rather than during render to satisfy
  // react-hooks/refs.
  const shouldPollRef = useRef(true);
  const shouldSkip = options.skip || !options.variables?.id?.trim();
  useEffect(() => {
    shouldPollRef.current = isFocused && isActive;
  }, [isFocused, isActive]);

  return usePlayerDetailGetPlayerQuery({
    ...options,
    skip: shouldSkip,
    // `skip` alone does not stop an already-armed poll (SE-8573): Apollo 3.x
    // turns a skipped query into fetchPolicy "standby" but leaves its
    // pollInterval running, and each tick re-fetches as "network-only" with
    // the CURRENT variables. An id that went empty while a poll was in flight
    // was therefore sent as `id: ""` on every tick. Zeroing the interval
    // tears the timer down.
    pollInterval: shouldSkip ? 0 : 30000,
    errorPolicy: "all",
    skipPollAttempt: () => !shouldPollRef.current,
  });
};
